import React, { useState, useEffect } from 'react';
import NavigationBar from './NavigationBar';
import HamburgerMenu from './HamburgerMenu';
import Footer from './Footer';

function DifficultySelection({ navigateTo, goBack, isLoggedIn, attemptState, userProfile }) {
  const [modes, setModes] = useState({
    easy: 'standard',
    hard: 'standard',
    master: 'standard'
  });

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // ANTI-CHEAT: Sync mode toggles with attempt state on mount/update
  useEffect(() => {
    const newModes = {};
    
    Object.keys(attemptState).forEach(difficulty => {
      const state = attemptState[difficulty];
      
      if (state.completed) {
        // Completed: lock both modes (doesn't matter which is shown)
        newModes[difficulty] = state.mode || 'standard';
      } else if (state.used && state.mode === 'standard') {
        // Standard used: force relaxed
        newModes[difficulty] = 'relaxed';
      } else if (state.used && state.mode === 'relaxed') {
        // Relaxed used: lock at relaxed (both disabled anyway)
        newModes[difficulty] = 'relaxed';
      } else {
        // Not used: default to standard
        newModes[difficulty] = 'standard';
      }
    });
    
    setModes(newModes);
  }, [attemptState]);

  const difficulties = [
    {
      id: 'easy',
      name: 'Easy',
      icon: '🟢',
      description: 'Perfect for learning and building confidence',
      tagline: 'A calm, confidence-building solve'
    },
    {
      id: 'hard',
      name: 'Hard',
      icon: '🟠',
      description: 'Core competitive difficulty',
      tagline: 'Where speed and accuracy start to matter'
    },
    {
      id: 'master',
      name: 'Master',
      icon: '🔴',
      description: 'Minimal clues, advanced logic required',
      tagline: 'For focused, mistake-free solving'
    }
  ];

  // ANTI-CHEAT: Determine if toggle is enabled
  const isToggleEnabled = (difficulty) => {
    const state = attemptState[difficulty];
    
    // Completed: toggle disabled
    if (state.completed) return false;
    
    // Used in any mode: toggle disabled
    if (state.used) return false;
    
    // Not used: toggle enabled
    return true;
  };

  // ANTI-CHEAT: Determine if start button is enabled
  const isStartEnabled = (difficulty) => {
    const state = attemptState[difficulty];
    
    // Completed: cannot start
    if (state.completed) return false;
    
    // Not used: can start
    return true;
  };

  const handleModeToggle = (difficulty) => {
    // ANTI-CHEAT: Only allow toggle if not used/completed
    if (!isToggleEnabled(difficulty)) return;
    
    setModes(prev => ({
      ...prev,
      [difficulty]: prev[difficulty] === 'standard' ? 'relaxed' : 'standard'
    }));
  };

  const handleStartPuzzle = (difficulty, mode) => {
    // ANTI-CHEAT: Block if completed
    if (!isStartEnabled(difficulty.toLowerCase())) {
      return;
    }
    
    navigateTo('puzzle', { 
      difficulty: difficulty.toLowerCase(),
      mode: mode
    });
  };

  const getStatusBadge = (difficulty) => {
    const state = attemptState[difficulty];
    
    if (state.completed) {
      return (
        <div style={{
          display: 'inline-block',
          padding: '4px 12px',
          borderRadius: '12px',
          backgroundColor: '#D1FAE5',
          color: '#10B981',
          fontSize: '13px',
          fontWeight: '600',
          marginTop: '8px'
        }}>
          Completed Today
        </div>
      );
    }
    
    if (state.used) {
      return (
        <div style={{
          display: 'inline-block',
          padding: '4px 12px',
          borderRadius: '12px',
          backgroundColor: '#FEF3C7',
          color: '#F59E0B',
          fontSize: '13px',
          fontWeight: '600',
          marginTop: '8px'
        }}>
          Attempt Used
        </div>
      );
    }
    
    return null;
  };

  const getModeToggleStyle = (difficulty) => {
    const state = attemptState[difficulty];
    const isEnabled = isToggleEnabled(difficulty);
    
    // Base opacity for disabled state
    const opacity = isEnabled ? 1 : 0.5;
    
    // Determine colors based on state
    let bgColor, borderColor;
    
    if (state.completed) {
      // Completed: neutral gray
      bgColor = '#F3F4F6';
      borderColor = '#D1D5DB';
    } else if (state.used && state.mode === 'standard') {
      // Standard used, forced to relaxed
      bgColor = '#FEF3C7';
      borderColor = '#F59E0B';
    } else if (state.used && state.mode === 'relaxed') {
      // Relaxed used, locked
      bgColor = '#FEF3C7';
      borderColor = '#F59E0B';
    } else {
      // Normal selectable state
      bgColor = modes[difficulty] === 'standard' ? '#EFF6FF' : '#FEF3C7';
      borderColor = modes[difficulty] === 'standard' ? '#2563EB' : '#F59E0B';
    }
    
    return { opacity, bgColor, borderColor, isEnabled };
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #1e3a8a 0%, #1e40af 100%)',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      display: 'flex',
      flexDirection: 'column'
    }}>
      {!isMobile && <NavigationBar currentPage="difficulty" isLoggedIn={isLoggedIn} navigateTo={navigateTo} userProfile={userProfile} />}
      {isMobile && <HamburgerMenu currentPage="difficulty" isLoggedIn={isLoggedIn} navigateTo={navigateTo} userProfile={userProfile} />}

      <div style={{ flex: '1', padding: '40px 20px' }}>
      <div style={{
        maxWidth: '1000px',
        margin: '0 auto',
        paddingTop: !isMobile ? '20px' : '0'
      }}>
        <div style={{
          textAlign: 'center',
          marginBottom: '40px'
        }}>
          <h1 style={{
            color: 'white',
            fontSize: '36px',
            fontWeight: '800',
            margin: '0 0 12px 0'
          }}>
            Choose Your Difficulty
          </h1>
          <p style={{
            color: 'rgba(255, 255, 255, 0.8)',
            fontSize: '18px',
            margin: '0'
          }}>
            Everyone solves the same puzzle. How will you compare?
          </p>
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: '24px',
          marginBottom: '40px'
        }}>
          {difficulties.map(diff => {
            const toggleStyle = getModeToggleStyle(diff.id);
            const startEnabled = isStartEnabled(diff.id);
            
            return (
              <div
                key={diff.id}
                style={{
                  background: 'white',
                  borderRadius: '16px',
                  padding: '28px',
                  boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)',
                  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                  cursor: startEnabled ? 'pointer' : 'default',
                  display: 'flex',
                  flexDirection: 'column',
                  opacity: startEnabled ? 1 : 0.6
                }}
                onMouseEnter={(e) => {
                  if (startEnabled) {
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.boxShadow = '0 15px 50px rgba(0, 0, 0, 0.3)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (startEnabled) {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 10px 40px rgba(0, 0, 0, 0.2)';
                  }
                }}
              >
                <div style={{ marginBottom: '16px' }}>
                  <div style={{
                    fontSize: '40px',
                    marginBottom: '8px'
                  }}>
                    {diff.icon}
                  </div>
                  <h2 style={{
                    fontSize: '28px',
                    fontWeight: '800',
                    margin: '0 0 8px 0',
                    color: '#1F2937'
                  }}>
                    {diff.name}
                  </h2>
                  <p style={{
                    fontSize: '15px',
                    color: '#6B7280',
                    margin: diff.id === 'easy' ? '0 0 0px 0' : '0 0 12px 0',
                    lineHeight: '1.5'
                  }}>
                    {diff.description}
                  </p>
                  
                  {getStatusBadge(diff.id)}
                </div>

                <div style={{
                  background: '#F9FAFB',
                  padding: '12px',
                  borderRadius: '8px',
                  marginBottom: diff.id === 'easy' ? '12px' : '20px',
                  flex: '0 0 auto'
                }}>
                  <div style={{
                    fontSize: '14px',
                    color: '#4B5563',
                    fontStyle: 'italic',
                    lineHeight: '1.5',
                    minHeight: '42px',
                    display: 'flex',
                    alignItems: 'center'
                  }}>
                    {diff.tagline}
                  </div>
                </div>

                <div style={{
                  marginBottom: '20px'
                }}>
                  <label style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#374151',
                    display: 'block',
                    marginBottom: '8px'
                  }}>
                    Solve Mode
                  </label>
                  
                  <div
                    onClick={() => handleModeToggle(diff.id)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      cursor: toggleStyle.isEnabled ? 'pointer' : 'not-allowed',
                      padding: '12px',
                      background: toggleStyle.bgColor,
                      border: `2px solid ${toggleStyle.borderColor}`,
                      borderRadius: '8px',
                      transition: 'all 0.2s ease',
                      opacity: toggleStyle.opacity
                    }}
                  >
                    <div style={{
                      width: '44px',
                      height: '24px',
                      background: modes[diff.id] === 'standard' ? '#2563EB' : '#D1D5DB',
                      borderRadius: '12px',
                      position: 'relative',
                      transition: 'background 0.2s ease'
                    }}>
                      <div style={{
                        width: '18px',
                        height: '18px',
                        background: 'white',
                        borderRadius: '50%',
                        position: 'absolute',
                        top: '3px',
                        left: modes[diff.id] === 'standard' ? '23px' : '3px',
                        transition: 'left 0.2s ease',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                      }} />
                    </div>

                    <div style={{ flex: 1 }}>
                      <div style={{
                        fontSize: '15px',
                        fontWeight: '600',
                        color: '#1F2937'
                      }}>
                        {modes[diff.id] === 'standard' ? 'Standard' : 'Relaxed'}
                      </div>
                      <div style={{
                        fontSize: '12px',
                        color: '#6B7280',
                        marginTop: '2px'
                      }}>
                        {modes[diff.id] === 'standard' 
                          ? 'Leaderboard eligible' 
                          : 'Solve freely, no pressure'}
                      </div>
                    </div>
                  </div>
                </div>

                <button
                  onClick={() => handleStartPuzzle(diff.name, modes[diff.id])}
                  disabled={!startEnabled}
                  style={{
                    width: '100%',
                    background: startEnabled ? '#FF8C00' : '#D1D5DB',
                    color: 'white',
                    border: 'none',
                    padding: '14px 24px',
                    fontSize: '16px',
                    fontWeight: '700',
                    borderRadius: '10px',
                    cursor: startEnabled ? 'pointer' : 'not-allowed',
                    transition: 'all 0.2s ease',
                    boxShadow: startEnabled ? '0 4px 12px rgba(255, 140, 0, 0.3)' : 'none'
                  }}
                  onMouseEnter={(e) => {
                    if (startEnabled) {
                      e.target.style.background = '#E07B00';
                      e.target.style.transform = 'translateY(-2px)';
                      e.target.style.boxShadow = '0 6px 16px rgba(255, 140, 0, 0.4)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (startEnabled) {
                      e.target.style.background = '#FF8C00';
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.boxShadow = '0 4px 12px rgba(255, 140, 0, 0.3)';
                    }
                  }}
                >
                  {startEnabled ? 'Start Puzzle →' : 'Completed'}
                </button>
              </div>
            );
          })}
        </div>

        <div style={{
          maxWidth: '1000px',
          margin: '0 auto',
          background: 'rgba(255, 255, 255, 0.1)',
          backdropFilter: 'blur(10px)',
          borderRadius: '12px',
          padding: '20px 24px',
          border: '1px solid rgba(255, 255, 255, 0.2)'
        }}>
          <div style={{
            color: 'white',
            fontSize: '14px',
            lineHeight: '1.6'
          }}>
            <strong>Standard mode:</strong> Timer starts immediately, 3 mistake limit, leaderboard eligible, fair-play rules apply.
            <br />
            <strong>Relaxed mode:</strong> Casual play with no time pressure or limits. Not ranked.
          </div>
        </div>
        </div>
      </div>

      <Footer navigateTo={navigateTo} />
    </div>
  );
}

export default DifficultySelection;
